<template>
  <div class="sm:min-w-[350px] relative z-10">
    <div
      class="h-full bg-white dark:bg-[#8542b41d] border border-[#9857c81A] !backdrop-blur-md rounded-2xl overflow-hidden"
    >
      <!-- Header -->
      <div class="bg-[#501d71] p-6">
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <svg
              class="w-5 h-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
          </div>
          <div>
            <p class="text-xl font-bold text-white">Filter Services</p>
            <p class="text-purple-100 text-sm">Find exactly what you need</p>
          </div>
        </div>

        <!-- Active Filters Count -->
        <div class="flex justify-between gap-4 mt-4 items-center">
          <div v-if="activeFiltersCount > 0">
            <div
              class="inline-flex items-center px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium"
            >
              <span
                class="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"
              ></span>
              {{ activeFiltersCount }} filter{{
                activeFiltersCount !== 1 ? "s" : ""
              }}
              active
            </div>
          </div>

          <!-- Clear Filters Button -->
          <div v-if="activeFiltersCount > 0">
            <button
              @click="clearAllFilters"
              class="w-full flex items-center justify-center gap-2 px-2 py-1 text-xs bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300 font-semibold rounded-xl border border-red-200 dark:border-red-800 transition-all duration-300 hover:shadow-lg group"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Clear All Filters
            </button>
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Categories Section -->
        <div class="mb-3">
          <div class="flex items-center justify-between mb-6 pr-3">
            <h4
              class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
            >
              <div
                class="w-2 h-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
              ></div>
              Categories
            </h4>
            <span
              class="text-xs font-medium bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 text-[var(--primary)] px-2 py-1 rounded-full"
            >
              {{ visibleCategories.length + hiddenCategories.length }}
            </span>
          </div>

          <div class="space-y-3">
            <!-- Visible Categories -->
            <div
              v-for="(category, index) in visibleCategories"
              :key="category.name"
              class="group relative"
            >
              <div
                class="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              ></div>
              <label
                class="relative flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <!-- Custom Checkbox -->
                <div class="relative flex-shrink-0 mr-3">
                  <input
                    :id="category.name"
                    v-model="category.checked"
                    type="checkbox"
                    class="sr-only"
                  />
                  <div
                    :class="[
                      'w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center',
                      category.checked
                        ? 'bg-[var(--primary)] border-transparent shadow-lg'
                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500',
                    ]"
                  >
                    <svg
                      v-if="category.checked"
                      class="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>

                <!-- Category Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between">
                    <span
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-[var(--primary)] transition-colors duration-200"
                    >
                      {{ category.name }}
                    </span>
                    <span
                      :class="[
                        'text-xs px-2 py-1 rounded-full font-medium transition-all duration-200 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 text-[var(--primary)]'
                      ]"
                    >
                      {{ category.count }}
                    </span>
                  </div>
                </div>
              </label>
            </div>

            <!-- Expandable Categories -->
            <transition-group name="category" tag="div" class="space-y-3">
              <div
                v-for="(category, index) in showMoreCategories
                  ? hiddenCategories
                  : []"
                :key="category.name"
                class="group relative"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                ></div>
                <label
                  class="relative flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <!-- Custom Checkbox -->
                  <div class="relative flex-shrink-0 mr-3">
                    <input
                      :id="category.name"
                      v-model="category.checked"
                      type="checkbox"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center',
                        category.checked
                          ? 'bg-[var(--primary)] border-transparent shadow-lg'
                          : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500',
                      ]"
                    >
                      <svg
                        v-if="category.checked"
                        class="w-3 h-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  </div>

                  <!-- Category Info -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between">
                      <span
                        class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200"
                      >
                        {{ category.name }}
                      </span>
                      <span
                        :class="[
                          'text-xs px-2 py-1 rounded-full font-medium transition-all duration-200 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 text-[var(--primary)]',
                        ]"
                      >
                        {{ category.count }}
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            </transition-group>

            <!-- Show More/Less Button -->
            <button
              v-if="hiddenCategories.length > 0"
              @click="toggleMoreCategories(showMoreCategories)"
              class="w-full flex items-center justify-center gap-2 p-3 mt-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 hover:from-purple-100 hover:to-blue-100 dark:hover:from-purple-900/40 dark:hover:to-blue-900/40 text-purple-700 dark:text-purple-300 font-semibold rounded-xl border border-purple-200 dark:border-purple-800 transition-all duration-300 hover:shadow-lg group"
            >
              <span class="text-sm">
                <span v-if="!showMoreCategories">
                  Show {{ hiddenCategories.length }} more
                </span>
                <span v-else>Show less</span>
              </span>

              <div class="relative">
                <div
                  class="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full blur-sm opacity-30 group-hover:opacity-50 transition-opacity duration-200"
                ></div>
                <div
                  class="relative w-5 h-5 bg-[var(--primary)] rounded-full flex items-center justify-center"
                >
                  <svg
                    :class="{ 'rotate-180': showMoreCategories }"
                    class="w-3 h-3 text-white transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </div>

        <!-- Technologies Section -->
        <div v-if="!appendClass">
          <div
            class="flex items-center justify-between mb-6 pt-6 border-t border-gray-200 dark:border-gray-700 pr-3"
          >
            <h4
              class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
            >
              <div
                class="w-2 h-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
              ></div>
              Technologies
            </h4>
            <span
              class="text-xs px-2 py-1 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 text-[var(--primary)]"
            >
              {{ technologies.length }}
            </span>
          </div>

          <div class="grid grid-cols-1 gap-2">
            <NuxtLink
              v-for="tech in technologies"
              :key="tech.name"
              :to="tech.link"
              class="group flex items-center p-3 rounded-lg hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 dark:hover:from-purple-900/20 dark:hover:to-blue-900/20 transition-all duration-200 border border-transparent hover:border-purple-200 dark:hover:border-purple-800"
            >
              <!-- Technology Icon -->
              <div class="relative flex-shrink-0 mr-3">
                <div
                  class="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg blur-sm opacity-20 group-hover:opacity-40 transition-opacity duration-200"
                ></div>
                <div
                  class="relative w-10 h-10 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-100 dark:border-gray-700 flex items-center justify-center overflow-hidden"
                >
                  <div
                    :class="[
                      'bg-[url(/images/capabilities-icons.webp)] w-8 h-8 bg-white transition-transform duration-200 group-hover:scale-110',
                      tech.iconPosition,
                    ]"
                  />
                </div>
              </div>

              <!-- Technology Name -->
              <span
                class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200"
              >
                {{ tech.name }}
              </span>

              <!-- Arrow Icon -->
              <svg
                class="w-4 h-4 ml-auto text-gray-400 group-hover:text-purple-500 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-x-2 group-hover:translate-x-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

const props = defineProps<{
  allCategories: { name: string; checked: boolean; count: number }[];
  appendClass?: boolean;
}>();

const localCategories = ref([...props.allCategories]);

watch(
  () => props.allCategories,
  (newVal) => {
    localCategories.value = [...newVal];
  },
  { deep: true }
);

const showMoreCategories = ref(false);

// Categories data
const emit = defineEmits(["categoryFilter", "toggleMoreCategories"]);

// Technologies data
const technologies = ref([
  {
    name: "Laravel",
    iconPosition: "bg-[position:6px_-300px]",
    link: "/capabilities/laravel-development-company",
  },
  {
    name: "Vue.js",
    iconPosition: "bg-[position:-44px_-300px]",
    link: "/capabilities/vuejs-development-company",
  },
  {
    name: "MOR API",
    iconPosition: "bg-[position:-102px_-300px]",
    link: "/capabilities/mor-api-development-company",
  },
  {
    name: "Facebook API",
    iconPosition: "bg-[position:-158px_-300px]",
    link: "/capabilities/facebook-api-integration-company",
  },
  {
    name: "Paypal",
    iconPosition: "bg-[position:-204px_-300px]",
    link: "/capabilities/paypal-integration-services",
  },
  {
    name: "Google Calendar API",
    iconPosition: "bg-[position:-260px_-300px]",
    link: "/capabilities/google-calendar-api-integration-services",
  },
  {
    name: "Highcharts",
    iconPosition: "bg-[position:6px_-372px]",
    link: "/capabilities/highcharts-api-integration-solutions",
  },
  {
    name: "Sendgrid",
    iconPosition: "bg-[position:-44px_-372px]",
    link: "/capabilities/sendgrid",
  },
  {
    name: "AJAX",
    iconPosition: "bg-[position:-100px_-366px]",
    link: "/capabilities/ajax-development-company",
  },
  {
    name: "Adonisjs",
    iconPosition: "bg-[position:-156px_-372px]",
    link: "/capabilities/adonisjs-nodejs-development-company",
  },
  {
    name: "Mailgun",
    iconPosition: "bg-[position:-208px_-370px]",
    link: "/capabilities/mailgun-api-integration-company",
  },
  {
    name: "Stripe",
    iconPosition: "bg-[position:-260px_-372px]",
    link: "/capabilities/stripe-integration-company",
  },
  {
    name: "Slack",
    iconPosition: "bg-[position:6px_-430px]",
    link: "/capabilities/slack-integration-company",
  },
  {
    name: "Push Notifications",
    iconPosition: "bg-[position:-42px_-430px]",
    link: "/capabilities/push-notifications-integration-company",
  },
  {
    name: "Pusher",
    link: "/capabilities/pusher-integration-company",
    iconPosition: "bg-[position:-138px_-430px]",
  },
  {
    name: "MongoDb",
    link: "/capabilities/mongodb-development-company",
    iconPosition: "bg-[position:-90px_-430px]",
  },
  {
    name: "2 Checkout",
    link: "/capabilities/2checkout-payment-gateway-integration",
    iconPosition: "bg-[position:-186px_-430px]",
  },
]);

// Computed properties
const visibleCategories = computed(() => {
  return localCategories.value.slice(0, 5);
});

const hiddenCategories = computed(() => {
  return localCategories.value.slice(5);
});

const activeFiltersCount = computed(() => {
  return localCategories.value.filter((cat) => cat.checked).length;
});

// Methods
const toggleMoreCategories = (value: boolean) => {
  emit("toggleMoreCategories", value);
  showMoreCategories.value = !showMoreCategories.value;
};

const clearAllFilters = () => {
  localCategories.value.forEach((category) => {
    category.checked = false;
  });
};

watch(localCategories.value, () => {
  const filteredCategory = localCategories.value?.filter(
    (category) => category.checked
  );
  emit("categoryFilter", filteredCategory);
});
</script>

<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgb(147 51 234) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, rgb(147 51 234), rgb(59 130 246));
  border-radius: 2px;
}

.category-enter-active,
.category-leave-active {
  transition: all 0.3s ease;
}

.category-enter-from,
.category-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
